import type {
  CrmIntegrationAttachment,
  CrmIntegrationAttachmentKind,
  CrmIntegrationAttachmentProvider,
} from '../types/crm-integration-chat.types'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'

const ATTACHMENT_KINDS: CrmIntegrationAttachmentKind[] = [
  'image',
  'file',
  'video',
  'audio',
  'voice',
  'animation',
  'unknown',
]

const isAttachmentKind = (value: unknown): value is CrmIntegrationAttachmentKind =>
  typeof value === 'string' &&
  ATTACHMENT_KINDS.includes(value as CrmIntegrationAttachmentKind)

const isAttachmentProvider = (
  value: unknown,
): value is CrmIntegrationAttachmentProvider =>
  value === 'max' ||
  value === 'telegram' ||
  value === 'vkontakte' ||
  value === 'wazzup'

/** Wazzup часто без `kind` — выводим по MIME. */
const inferAttachmentKindFromMime = (
  mime: string | null,
): CrmIntegrationAttachmentKind | null => {
  if (!mime) {
    return null
  }

  const normalized = mime.trim().toLowerCase()
  if (!normalized || normalized === 'application/octet-stream') {
    return null
  }

  if (normalized.startsWith('image/')) {
    return 'image'
  }
  if (normalized.startsWith('video/')) {
    return 'video'
  }
  if (normalized.startsWith('audio/')) {
    return 'audio'
  }

  return 'file'
}

/** Legacy VK raw до normalized контракта — в TG/MAX чате пропускаем. */
export const isLegacyVkIntegrationAttachment = (raw: unknown): boolean => {
  if (!raw || typeof raw !== 'object') {
    return false
  }

  const record = raw as Record<string, unknown>
  return 'type' in record && !('kind' in record)
}

const extractUploadPath = (raw: string): string | null => {
  const trimmed = raw.trim()
  if (!trimmed) {
    return null
  }

  if (trimmed.startsWith('/upload/')) {
    return trimmed
  }

  try {
    const parsed = new URL(trimmed)
    if (parsed.pathname.startsWith('/upload/')) {
      return parsed.pathname
    }
  } catch {
    return null
  }

  return null
}

/**
 * URL для <img> в UI: user_files с диска API (REACT_APP_PATH_FILE), не ngrok для send.
 * Signed /v1/... и внешние CDN-ссылки — as-is.
 */
export const resolveCrmIntegrationAttachmentUrlForDisplay = (
  raw: string | null | undefined,
): string | null => {
  if (!raw?.trim()) {
    return null
  }

  const trimmed = raw.trim()

  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
    return trimmed
  }

  const uploadPath = extractUploadPath(trimmed)
  if (uploadPath) {
    const localUrl = getCorrectFilePath(uploadPath)
    if (localUrl) {
      return localUrl
    }
  }

  return resolveCrmIntegrationAttachmentUrl(trimmed)
}

/** Абсолютный URL as-is; legacy relative `/v1/...` → origin + path. */
export const resolveCrmIntegrationAttachmentUrl = (
  raw: string | null | undefined,
): string | null => {
  if (!raw?.trim()) {
    return null
  }

  const trimmed = raw.trim()

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed
  }

  if (trimmed.startsWith('/')) {
    const apiBase = process.env.REACT_APP_URL_API || ''

    try {
      return `${new URL(apiBase).origin}${trimmed}`
    } catch {
      const base = apiBase.replace(/\/$/, '')
      return `${base}${trimmed}`
    }
  }

  return trimmed
}

const readOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const readOptionalUserFileId = (value: unknown): number | null => {
  const fileId = Number(value)
  return Number.isFinite(fileId) && fileId > 0 ? fileId : null
}

const readOptionalNumber = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** url → preview_url; бэк гарантирует url в GET messages */
export const attachmentImageSrc = (
  att: CrmIntegrationAttachment,
): string | null => {
  if (att.kind !== 'image') {
    return null
  }

  return resolveCrmIntegrationAttachmentUrlForDisplay(att.url || att.preview_url)
}

export const attachmentImageFullUrl = (
  att: CrmIntegrationAttachment,
): string | null => {
  if (att.kind !== 'image') {
    return null
  }

  const full = resolveCrmIntegrationAttachmentUrlForDisplay(att.url)
  const preview = attachmentImageSrc(att)
  return full ?? preview
}

export const normalizeCrmIntegrationAttachment = (
  raw: unknown,
): CrmIntegrationAttachment | null => {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  if (isLegacyVkIntegrationAttachment(raw)) {
    return null
  }

  const record = raw as Record<string, unknown>
  const mime = readOptionalString(record.mime)
  const url = readOptionalString(record.url)
  const previewUrl = readOptionalString(record.preview_url)

  let kind: CrmIntegrationAttachmentKind | null = isAttachmentKind(record.kind)
    ? record.kind
    : null

  if (!kind) {
    kind = inferAttachmentKindFromMime(mime)
  }

  // Есть контент без kind/mime — generic file chip, не дропаем вложение.
  if (!kind && (url || previewUrl)) {
    kind = 'unknown'
  }

  if (!kind) {
    return null
  }

  const provider = isAttachmentProvider(record.provider)
    ? record.provider
    : null

  const providerMeta =
    record.provider_meta && typeof record.provider_meta === 'object'
      ? (record.provider_meta as CrmIntegrationAttachment['provider_meta'])
      : undefined

  const userFileId =
    readOptionalUserFileId(record.user_file_id) ??
    (providerMeta ? readOptionalUserFileId(providerMeta.user_file_id) : null)

  const sizeRaw = record.size
  const size =
    typeof sizeRaw === 'number' && Number.isFinite(sizeRaw)
      ? sizeRaw
      : typeof sizeRaw === 'string' && sizeRaw.trim()
        ? readOptionalNumber(sizeRaw)
        : null

  return {
    kind,
    url,
    preview_url: previewUrl,
    user_file_id: userFileId,
    name: readOptionalString(record.name),
    mime,
    size,
    duration:
      typeof record.duration === 'number' && Number.isFinite(record.duration)
        ? record.duration
        : typeof record.duration === 'string'
          ? readOptionalNumber(record.duration)
          : null,
    provider,
    provider_meta: providerMeta,
  }
}

export const normalizeCrmIntegrationAttachments = (
  raw: unknown,
): CrmIntegrationAttachment[] =>
  (Array.isArray(raw) ? raw : [])
    .map(normalizeCrmIntegrationAttachment)
    .filter((item): item is CrmIntegrationAttachment => item !== null)

const CRM_INTEGRATION_SUPPORTED_ATTACHMENT_KINDS = new Set<
  CrmIntegrationAttachment['kind']
>(['image', 'file', 'video', 'voice', 'audio', 'unknown'])

export const partitionCrmIntegrationAttachments = (
  attachments: CrmIntegrationAttachment[],
): {
  images: CrmIntegrationAttachment[]
  files: CrmIntegrationAttachment[]
  videos: CrmIntegrationAttachment[]
  voices: CrmIntegrationAttachment[]
  audios: CrmIntegrationAttachment[]
  unsupported: CrmIntegrationAttachment[]
} => {
  const images = attachments.filter((item) => item.kind === 'image')
  const videos = attachments.filter((item) => item.kind === 'video')
  const voices = attachments.filter((item) => item.kind === 'voice')
  const audios = attachments.filter((item) => item.kind === 'audio')
  // unknown с url — как файл (дока Wazzup/fallback); без url — unsupported.
  const files = attachments.filter(
    (item) =>
      item.kind === 'file' ||
      (item.kind === 'unknown' && Boolean(item.url?.trim())),
  )
  const unsupported = attachments.filter(
    (item) => !CRM_INTEGRATION_SUPPORTED_ATTACHMENT_KINDS.has(item.kind),
  )

  return { images, files, videos, voices, audios, unsupported }
}

const getAttachmentFileExtension = (value: string | null | undefined): string => {
  if (!value?.trim()) {
    return ''
  }

  const match = value.trim().match(/\.([^.?#/]+)(?:[?#].*)?$/i)
  return match ? match[1].toLowerCase() : ''
}

const MIME_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'text/plain': 'TXT',
  'text/csv': 'CSV',
  'application/rtf': 'RTF',
  'application/zip': 'ZIP',
  'application/x-rar-compressed': 'RAR',
  'application/x-7z-compressed': '7Z',
}

export const formatCrmIntegrationAttachmentBytes = (
  sizeBytes: number | null | undefined,
): string | null => {
  if (typeof sizeBytes !== 'number' || !Number.isFinite(sizeBytes) || sizeBytes < 0) {
    return null
  }

  if (sizeBytes < 1024) {
    return `${sizeBytes} Б`
  }

  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} КБ`
  }

  if (sizeBytes < 1024 * 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} МБ`
  }

  return `${(sizeBytes / (1024 * 1024 * 1024)).toFixed(1)} ГБ`
}

export const getCrmIntegrationAttachmentTypeLabel = (
  att: CrmIntegrationAttachment,
): string | null => {
  const mime = att.mime?.trim().toLowerCase()
  if (mime) {
    if (MIME_TYPE_LABELS[mime]) {
      return MIME_TYPE_LABELS[mime]
    }

    if (mime.includes('pdf')) {
      return 'PDF'
    }

    const subtype = mime.split('/').pop()
    if (subtype) {
      return subtype.toUpperCase()
    }
  }

  const extension =
    getAttachmentFileExtension(att.name) || getAttachmentFileExtension(att.url)

  return extension ? extension.toUpperCase() : null
}

const CRM_INTEGRATION_ATTACHMENT_KIND_LABELS: Record<
  CrmIntegrationAttachment['kind'],
  string
> = {
  image: 'Изображение',
  file: 'Файл',
  video: 'Видео',
  voice: 'Голосовое сообщение',
  audio: 'Аудиозапись',
  animation: 'GIF',
  unknown: 'Вложение',
}

export const getCrmIntegrationAttachmentKindLabel = (
  att: CrmIntegrationAttachment,
): string => CRM_INTEGRATION_ATTACHMENT_KIND_LABELS[att.kind] ?? 'Вложение'

export const attachmentUnsupportedMetaLabel = (
  att: CrmIntegrationAttachment,
): string | null => {
  const name = att.name?.trim()
  const typeLabel = getCrmIntegrationAttachmentTypeLabel(att)
  const durationLabel = formatCrmIntegrationAttachmentDuration(att.duration)
  const sizeLabel = formatCrmIntegrationAttachmentBytes(att.size)

  const parts = [name, typeLabel, durationLabel, sizeLabel].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

export const attachmentFileDisplayName = (
  att: CrmIntegrationAttachment,
): string => {
  const name = att.name?.trim()
  if (name) {
    return name
  }

  const extension = getAttachmentFileExtension(att.url)
  if (extension) {
    return `Файл.${extension}`
  }

  return 'Файл'
}

export const attachmentFileMetaLabel = (
  att: CrmIntegrationAttachment,
): string | null => {
  const typeLabel = getCrmIntegrationAttachmentTypeLabel(att)
  const sizeLabel = formatCrmIntegrationAttachmentBytes(att.size)

  if (typeLabel && sizeLabel) {
    return `${typeLabel} · ${sizeLabel}`
  }

  return typeLabel ?? sizeLabel
}

/** URL для скачивания file/unknown-вложения (CDN Wazzup/VK/MAX, signed proxy TG). */
export const attachmentFileHref = (
  att: CrmIntegrationAttachment,
): string | null => {
  if (att.kind !== 'file' && att.kind !== 'unknown') {
    return null
  }

  return (
    resolveCrmIntegrationAttachmentUrlForDisplay(att.url) ??
    resolveCrmIntegrationAttachmentUrl(att.url)
  )
}

/** URL для `<video src>`: signed proxy TG, CDN MAX/Wazzup, /upload/ для outgoing. */
export const attachmentVideoSrc = (
  att: CrmIntegrationAttachment,
): string | null => {
  if (att.kind !== 'video') {
    return null
  }

  return (
    resolveCrmIntegrationAttachmentUrlForDisplay(att.url) ??
    resolveCrmIntegrationAttachmentUrl(att.url)
  )
}

/** Poster/thumbnail для `<video poster>` или VK preview. */
export const attachmentVideoPoster = (
  att: CrmIntegrationAttachment,
): string | null => {
  if (att.kind !== 'video') {
    return null
  }

  return resolveCrmIntegrationAttachmentUrlForDisplay(att.preview_url)
}

export const attachmentVideoDisplayName = (
  att: CrmIntegrationAttachment,
): string => {
  const name = att.name?.trim()
  if (name) {
    return name
  }

  return 'Видео'
}

export const formatCrmIntegrationAttachmentDuration = (
  durationSeconds: number | null | undefined,
): string | null => {
  if (
    typeof durationSeconds !== 'number' ||
    !Number.isFinite(durationSeconds) ||
    durationSeconds < 0
  ) {
    return null
  }

  const totalSeconds = Math.round(durationSeconds)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export const attachmentVideoMetaLabel = (
  att: CrmIntegrationAttachment,
): string | null => {
  const durationLabel = formatCrmIntegrationAttachmentDuration(att.duration)
  const sizeLabel = formatCrmIntegrationAttachmentBytes(att.size)
  const typeLabel = getCrmIntegrationAttachmentTypeLabel(att)

  const parts = [typeLabel, durationLabel, sizeLabel].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** Ссылка на видео ВК из unified `provider_meta`. */
export const attachmentVkVideoExternalUrl = (
  att: CrmIntegrationAttachment,
): string | null => {
  if (att.kind !== 'video' || att.provider !== 'vkontakte') {
    return null
  }

  const meta = att.provider_meta
  if (!meta) {
    return null
  }

  const ownerId = readOptionalNumber(meta.owner_id)
  const videoId = readOptionalNumber(meta.id)

  if (ownerId == null || videoId == null) {
    return null
  }

  return `https://vk.com/video${ownerId}_${videoId}`
}

const normalizeVoiceWaveform = (value: unknown): number[] | null => {
  if (!Array.isArray(value) || value.length === 0) {
    return null
  }

  const bars = value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item >= 0 && item <= 31)

  return bars.length > 0 ? bars : null
}

/** URL для `<audio src>`: только voice (не music/audio HLS). */
export const attachmentVoiceSrc = (
  att: CrmIntegrationAttachment,
): string | null => {
  if (att.kind !== 'voice') {
    return null
  }

  return (
    resolveCrmIntegrationAttachmentUrlForDisplay(att.url) ??
    resolveCrmIntegrationAttachmentUrl(att.url)
  )
}

/** Ссылка на музыкальный трек VK: audio{owner_id}_{id}. */
export const buildVkAudioPageUrl = (
  ownerId: number,
  audioId: number,
): string => `https://vk.com/audio${ownerId}_${audioId}`

const isHlsAudioUrl = (url: string | null | undefined): boolean => {
  if (!url?.trim()) {
    return false
  }
  return /\.m3u8(?:[?#]|$)/i.test(url.trim())
}

/** Подпись карточки music/audio. */
export const attachmentAudioDisplayName = (
  att: CrmIntegrationAttachment,
): string => {
  const name = att.name?.trim()
  return name || 'Аудио'
}

/**
 * Href для карточки kind=audio.
 * VK music — страница трека (m3u8 в <audio> не играет).
 */
export const attachmentAudioHref = (
  att: CrmIntegrationAttachment,
): string | null => {
  if (att.kind !== 'audio') {
    return null
  }

  const ownerId = readOptionalNumber(att.provider_meta?.owner_id)
  const audioId = readOptionalNumber(att.provider_meta?.id)
  if (
    (att.provider === 'vkontakte' || att.provider_meta?.type === 'audio') &&
    ownerId != null &&
    audioId != null
  ) {
    return buildVkAudioPageUrl(ownerId, audioId)
  }

  const directUrl =
    resolveCrmIntegrationAttachmentUrlForDisplay(att.url) ??
    resolveCrmIntegrationAttachmentUrl(att.url)

  if (!directUrl || isHlsAudioUrl(directUrl)) {
    return null
  }

  return directUrl
}

/** Waveform 0–31 из provider_meta (VK). */
export const attachmentVoiceWaveform = (
  att: CrmIntegrationAttachment,
): number[] | null => {
  if (att.kind !== 'voice') {
    return null
  }

  return normalizeVoiceWaveform(att.provider_meta?.waveform)
}
